/**
 * Auto-capitalization: a name or place field is put in Title Case when it
 * loses focus ("rosa delgado" → "Rosa Delgado", "o'neil" → "O'Neil"),
 * keeping any capital already typed ("McDonald" stays).
 *
 * One document-level listener rather than a prop on every input, so the
 * dozens of student, guardian, staff, visitor and address forms are covered
 * without each being edited. A field is a name field when its label says so
 * (see `isNameLabel`), or when it opts in with `data-titlecase`; it opts out
 * with `data-titlecase="off"`. Emails, usernames, passwords, IDs, numbers and
 * notes never match.
 *
 * Runs in the capture phase, ahead of React's own blur handlers, so a form
 * that saves on blur saves the capitalized value.
 */

import { isNameLabel, titleCaseName } from '@cornerstone/education-domain'

const EXCLUDED_AUTOCOMPLETE = /username|email|password|one-time-code|tel|url|postal|cc-/

/** The text that labels a field: aria-label, its <label>, the field wrapper's label, or the placeholder. */
function labelOf(input: HTMLInputElement): string {
  const aria = input.getAttribute('aria-label')
  if (aria) return aria
  const label = (input.id && document.querySelector<HTMLLabelElement>(`label[for="${CSS.escape(input.id)}"]`)) || input.closest('label')
  if (label) {
    // The label's own words, not the options of a select nested inside it.
    const copy = label.cloneNode(true) as HTMLElement
    copy.querySelectorAll('input, select, textarea, option, button').forEach((node) => node.remove())
    const text = copy.textContent?.trim()
    if (text) return text
  }
  const wrapped = input.closest('.field, .hlt-field, .ops-field, .cal-field')?.querySelector('.field__label')?.textContent
  return wrapped || input.placeholder || ''
}

export function isNameField(target: EventTarget | null): target is HTMLInputElement {
  if (!(target instanceof HTMLInputElement)) return false
  if (target.type !== 'text' || target.readOnly || target.disabled) return false
  const opt = target.dataset.titlecase
  if (opt === 'off') return false
  if (opt !== undefined) return true
  if (EXCLUDED_AUTOCOMPLETE.test(target.autocomplete)) return false
  return isNameLabel(labelOf(target))
}

/** Sets a React-controlled input's value so its onChange sees it. */
function setNativeValue(input: HTMLInputElement, value: string) {
  const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set
  setter?.call(input, value)
  input.dispatchEvent(new Event('input', { bubbles: true }))
}

/** Starts capitalizing name fields on blur; returns the function that stops it. */
export function installAutoCapitalize(): () => void {
  const onFocusOut = (event: FocusEvent) => {
    const input = event.target
    if (!isNameField(input)) return
    const next = titleCaseName(input.value)
    if (next !== input.value) setNativeValue(input, next)
  }
  document.addEventListener('focusout', onFocusOut, true)
  return () => document.removeEventListener('focusout', onFocusOut, true)
}
